import { Module } from 'vuex';
import {
  action,
  actionCreatorFactory,
  combineAction,
  combineMutation,
  mutation,
} from 'vuex-typescript-fsa/lib';
import { RootState } from '@/store';
import {
  CvCvSectionResponse,
  CvCvSection,
  CvSection,
  UpdateCvCvSectionsRequest,
  UpdateCvSectionDateItemOrderRequest,
  UpdateCvSectionSkillItemOrderRequest,
  UpdateCvSectionLinkItemOrderRequest,
  UpdateCvSectionGalleryItemOrderRequest,
  Cv,
  CvDesignTemplate,
} from '@/../gen';

interface ItemOrderRequest<T> {
  cv_id: number;
  cv_section_id: number;
  cv_section_item_id: number;
  body: T;
}

export const namespace = 'cv';
const actionCreator = actionCreatorFactory(namespace);

export const GetCv = actionCreator<{ cv_id: number }>('GET_CV');
export const GetCvCvSections = actionCreator<{ cv_id: number }>(
  'GET_CV_CV_SECTIONS'
);
export const SetCvCvSections = actionCreator<CvCvSectionResponse[]>(
  'SET_CV_CV_SECTIONS'
);
export const UpdateCvCvSections = actionCreator<{
  cv_id: number;
  cv_cv_section_id: number;
  body: UpdateCvCvSectionsRequest;
}>('UPDATE_CV_CV_SECTIONS');
export const UpdateCvSectionDateItemOrder = actionCreator<
  ItemOrderRequest<UpdateCvSectionDateItemOrderRequest>
>('UPDATE_CV_SECTION_DATE_ITEM_ORDER');
export const UpdateCvSectionSkillItemOrder = actionCreator<
  ItemOrderRequest<UpdateCvSectionSkillItemOrderRequest>
>('UPDATE_CV_SECTION_SKILL_ITEM_ORDER');
export const UpdateCvSectionLinkItemOrder = actionCreator<
  ItemOrderRequest<UpdateCvSectionLinkItemOrderRequest>
>('UPDATE_CV_SECTION_LINK_ITEM_ORDER');
export const UpdateCvSectionGalleryItemOrder = actionCreator<
  ItemOrderRequest<UpdateCvSectionGalleryItemOrderRequest>
>('UPDATE_CV_SECTION_GALLERY_ITEM_ORDER');

export const ResetState = actionCreator<null>('RESET_STATE');

export const CvFetched = actionCreator<Cv>('CV_FETCHED');
export const CvDesignTemplateFetched = actionCreator<CvDesignTemplate>(
  'CV_DESIGN_TEMPLATE_FETCHED'
);
export interface CvCvSectionWithSection extends CvCvSection {
  section: CvSection;
  sectionItems: any[];
}
export interface State {
  cv: Cv;
  cvSections: CvCvSectionWithSection[];
  cvDesignTemplate: CvDesignTemplate;
}

const initialState = (): State => {
  return {
    cv: null,
    cvSections: [],
    cvDesignTemplate: null,
  };
};

export const module: Module<State, RootState> = {
  namespaced: true,
  state: initialState,
  actions: combineAction(
    action(GetCv, async function(context, action) {
      const { data } = await this.proffApi.getCv(action.payload.cv_id);
      context.commit(CvFetched(data.data.attributes));
      context.commit(CvDesignTemplateFetched(data.included[0].attributes));
    }),
    action(GetCvCvSections, async function(context, action) {
      const { data } = await this.proffApi.listCvCvSections(
        action.payload.cv_id
      );
      context.commit(SetCvCvSections(data.data));
    }),
    action(UpdateCvCvSections, async function(context, action) {
      const { cv_id, cv_cv_section_id, body } = action.payload;
      const { data } = await this.proffApi.updateCvCvSections(
        cv_id,
        cv_cv_section_id,
        body
      );
      context.commit(SetCvCvSections(data.data));
    }),
    action(UpdateCvSectionDateItemOrder, async function(_context, action) {
      const { cv_id, cv_section_id, cv_section_item_id, body } = action.payload;
      await this.proffApi.updateCvSectionDateItemOrder(
        cv_id,
        cv_section_id,
        cv_section_item_id,
        body
      );
    }),
    action(UpdateCvSectionSkillItemOrder, async function(_context, action) {
      const { cv_id, cv_section_id, cv_section_item_id, body } = action.payload;
      await this.proffApi.updateCvSectionSkillItemOrder(
        cv_id,
        cv_section_id,
        cv_section_item_id,
        body
      );
    }),
    action(UpdateCvSectionLinkItemOrder, async function(_context, action) {
      const { cv_id, cv_section_id, cv_section_item_id, body } = action.payload;
      await this.proffApi.updateCvSectionLinkItemOrder(
        cv_id,
        cv_section_id,
        cv_section_item_id,
        body
      );
    }),
    action(UpdateCvSectionGalleryItemOrder, async function(_context, action) {
      const { cv_id, cv_section_id, cv_section_item_id, body } = action.payload;
      await this.proffApi.updateCvSectionGalleryItemOrder(
        cv_id,
        cv_section_id,
        cv_section_item_id,
        body
      );
    }),
    action(ResetState, function(context, action) {
      context.commit(ResetState(action.payload));
    })
  ),
  mutations: combineMutation(
    mutation(CvFetched, (state, action) => {
      state.cv = action.payload;
    }),
    mutation(SetCvCvSections, (state, action) => {
      state.cvSections = action.payload.map(item => {
        const { section, section_items } = item.links;
        return {
          ...item.attributes,
          // @ts-ignore FIXME
          section: section ? section.data.attributes : null,
          sectionItems: section_items
            ? // @ts-ignore FIXME
              section_items.data.map(sectionItem => sectionItem.attributes)
            : [],
        };
      });
    }),
    mutation(CvDesignTemplateFetched, (state, action) => {
      state.cvDesignTemplate = action.payload;
    }),
    mutation(ResetState, state => {
      Object.assign(state, initialState());
    })
  ),
  getters: {
    cv: state => state.cv,
    cvSections: state => state.cvSections,
    cvDesignTemplate: state => state.cvDesignTemplate,
  },
};
